import { box, type Aabb, type World } from '../sim.js';
import { PLAYER_RADIUS } from '../constants.js';
import type { Vec3 } from '../math.js';
import type { AreaDef, MapDef, NpcDef, WaypointGraph } from '../maps.js';
import { buildHeightfield, type Heightfield } from '../terrain.js';
import { slabRoom, pillarRow } from './kit.js';
import { LANTERNFALL_GRID, type RegionSidecar } from './region.js';

/**
 * Lanternfall - the persistent safe city, and the hub of the WoW-shaped slice. A walled square,
 * flat ground, centred on the origin so the one gate north into the Vigil Downs sits on a clean
 * axis. X is east, Z is south, y is up, yaw 0 faces -Z (north) - see `sim.ts`/`spine.ts`.
 *
 * Never a raid: no `hymnSpawns`, no `extracts`, no `raidDurationMs`. Persistent (`persistent:
 * true`) and capped at 500 (`maxPlayers`) because the room is never reaped and the perf gate
 * stands 300 bots in the plaza at once - which is also why the middle of the square is kept
 * empty: every building here hugs a wall or a corner, nothing sits within ~25 m of the origin
 * except the well (a curb, not an obstacle - see below).
 *
 * **The ground is a flat heightfield, not a bare `groundY`.** Every height on
 * `LANTERNFALL_GRID` is exactly 0, so every `y: 0` in this file means precisely what it meant
 * before the field existed and the sim cannot tell the difference. What it buys is the client's
 * terrain branch: `render/scene.ts` only attaches a region GLB for a map that has a heightfield,
 * and Lanternfall's dressing (`tools/assetgen/blender/lanternfall_region.py`) is a region GLB.
 * Any step in the city is a collider box; nothing is ever authored into the terrain here.
 *
 * With a region sidecar applied (`applyLanternfallRegion`), the Blender pass is authoritative for
 * the city's geometry: its colliders REPLACE the greybox below rather than adding to it, because
 * the Kenney town pieces it places stand exactly where these boxes did. Without one, the greybox
 * stands and the client draws it - the usual fail-soft.
 */

// -------------------------------------------------------------------------------------------
// Facing helper (same formula as spine.ts's npc table and the YARD's Isolde: yaw 0 faces -Z, so
// a facing toward a point is atan2(-dx, -dz)).
// -------------------------------------------------------------------------------------------
function faceTowards(pos: { x: number; z: number }, target: { x: number; z: number }): number {
  return Math.atan2(-(target.x - pos.x), -(target.z - pos.z));
}

/** The flat lattice. Built once: it is the same 1089 zeros for every caller. */
const FLAT_TERRAIN: Heightfield = buildHeightfield(
  LANTERNFALL_GRID.originX,
  LANTERNFALL_GRID.originZ,
  LANTERNFALL_GRID.cellSize,
  LANTERNFALL_GRID.cols,
  LANTERNFALL_GRID.rows,
  () => 0,
);

// ---------------------------------------------------------------------------------------------
// Fixed geometry constants. These are the plan Chris approved, and the Blender pass builds to
// exactly these numbers - so an NPC stand, an area cylinder or a waypoint written here still
// lands on the right piece of Kenney masonry once the GLB is in.
// ---------------------------------------------------------------------------------------------
const WALL_H = 4;
const WALL_T = 1;
/** The Lantern Hall - Isolde's quest hub, south side, door on its north face. */
const HALL_CX = 0;
const HALL_CZ = 44;
const HALL_W = 20;
const HALL_D = 14;
const HALL_H = 6;
/** Sabel's counter - a stall on the east side, facing the plaza. */
const STALL_X = 45;
const STALL_Z = -5;
/** The well: a low curb ring at the plaza's centre. */
const WELL_R = 3;
const WELL_H = 0.4;
const BENCH_R = 10;
const BENCH_N = 8;
/** The Graveyard - south-west corner. */
const GY_CX = -46;
const GY_CZ = 45;
/** Release point: beside the headstones, not on top of any of them. */
const GRAVEYARD_POS: Vec3 = { x: GY_CX, y: 0, z: GY_CZ };
const isoldePos: Vec3 = { x: 0, y: 0, z: 39 }; // just inside the Hall's north door (z=37)
const sabelPos: Vec3 = { x: STALL_X, y: 0, z: STALL_Z - 1.3 }; // behind the counter

/**
 * The greybox: what the city is when no Blender sidecar has been exported. Every box here has a
 * counterpart in `lanternfall_region.py`, which is why a sidecar replaces this list wholesale.
 */
function greyboxBoxes(): Aabb[] {
  const boxes: Aabb[] = [];

  // Perimeter wall: a 120 x 120 m square (x/z -60..60), 4 m tall, 1 m thick. The north wall (the
  // Downs side) carries a 10 m gap (x -5..5) - the Gate - wide enough that 500 players are not
  // funnelled through a doorway on their way out. South/east/west stay solid.
  boxes.push(
    box(-32.5, WALL_H / 2, -60, 55, WALL_H, WALL_T), // north, west of the gate (x -60..-5)
    box(32.5, WALL_H / 2, -60, 55, WALL_H, WALL_T), // north, east of the gate (x 5..60)
    box(0, WALL_H / 2, 60, 120, WALL_H, WALL_T), // south
    box(60, WALL_H / 2, 0, WALL_T, WALL_H, 120), // east
    box(-60, WALL_H / 2, 0, WALL_T, WALL_H, 120), // west
  );
  // A lamp either side of the gate's inner face, marking the threshold.
  boxes.push(box(-4, 1.5, -58, 0.3, 3, 0.3), box(4, 1.5, -58, 0.3, 3, 0.3));

  boxes.push(
    ...slabRoom(
      HALL_CX,
      HALL_CZ,
      HALL_W,
      HALL_D,
      HALL_H,
      0.4,
      [{ wall: 'n', center: 0, width: 6 }],
      {
        yBase: 0,
        roof: true,
      },
    ),
  );

  // Two side-street shops (flavour greybox, not Sabel's stall) - west and north-east, both facing
  // the plaza, keeping the centre open.
  boxes.push(
    ...slabRoom(-40, -20, 8, 8, 4, 0.3, [{ wall: 'e', center: -20, width: 3 }], {
      yBase: 0,
      roof: true,
    }),
  );
  boxes.push(
    ...slabRoom(40, -30, 8, 8, 4, 0.3, [{ wall: 'w', center: -30, width: 3 }], {
      yBase: 0,
      roof: true,
    }),
  );

  boxes.push(
    box(STALL_X, 0.5, STALL_Z, 4, 1, 1.2), // counter
    box(STALL_X - 1.7, 1.6, STALL_Z - 1, 0.2, 2.2, 0.2), // canopy post, left
    box(STALL_X + 1.7, 1.6, STALL_Z - 1, 0.2, 2.2, 0.2), // canopy post, right
    box(STALL_X, 2.75, STALL_Z - 0.5, 4.4, 0.15, 2.4), // canopy roof
  );

  // The well curb sits well inside `STEP_UP` (0.5 m) so it is a step, not a wall - walkable
  // straight over, never a trap. A square ring (not a rotated octagon: `box` is axis-aligned)
  // reads fine as a greybox kerb.
  boxes.push(
    box(0, WELL_H / 2, -WELL_R, 2 * WELL_R + 0.3, WELL_H, 0.3),
    box(0, WELL_H / 2, WELL_R, 2 * WELL_R + 0.3, WELL_H, 0.3),
    box(-WELL_R, WELL_H / 2, 0, 0.3, WELL_H, 2 * WELL_R + 0.3),
    box(WELL_R, WELL_H / 2, 0, 0.3, WELL_H, 2 * WELL_R + 0.3),
  );

  // Benches: 8 around the well, clear of its curb (radius ~3.2) at radius 10.
  for (let i = 0; i < BENCH_N; i++) {
    const a = (i / BENCH_N) * Math.PI * 2;
    boxes.push(box(Math.cos(a) * BENCH_R, 0.25, Math.sin(a) * BENCH_R, 1.2, 0.5, 0.5));
  }

  // The Graveyard: a curb outline plus 8 headstones (2 rows of 4), all clear of the corner walls
  // (x -60/z 60 sit 5+ m outside the plot).
  boxes.push(
    box(GY_CX, 0.15, GY_CZ - 6, 18.3, 0.3, 0.3), // north curb
    box(GY_CX, 0.15, GY_CZ + 6, 18.3, 0.3, 0.3), // south curb
    box(GY_CX - 9, 0.15, GY_CZ, 0.3, 0.3, 12.3), // west curb
    box(GY_CX + 9, 0.15, GY_CZ, 0.3, 0.3, 12.3), // east curb
  );
  for (const dz of [-3, 3]) {
    for (const dx of [-6, -2, 2, 6]) {
      boxes.push(box(GY_CX + dx, 0.4, GY_CZ + dz, 0.4, 0.8, 0.15));
    }
  }

  // West colonnade - a covered walkway along the inner west wall, flavour for the "side streets"
  // the brief calls for. Kept south of the graveyard plot (z <= 20).
  boxes.push(...pillarRow([-45], [-40, -20, 0, 20], 3, 0.5, 0));
  boxes.push(box(-45, 3.2, -10, 2, 0.3, 66)); // roof slab over the four pillars

  return boxes;
}

// ---------------------------------------------------------------------------------------------
// Spawns: rings around the plaza and out into the side streets, at least 2 m apart and never
// inside a box. Generated, then filtered against every box in the city (walls included) with a
// player-radius margin - so a sidecar that moves a building moves the spawns out of it too.
// ---------------------------------------------------------------------------------------------
function clearOf(bs: readonly Aabb[], x: number, z: number, r: number): boolean {
  for (const b of bs) {
    if (x > b.min.x - r && x < b.max.x + r && z > b.min.z - r && z < b.max.z + r) return false;
  }
  return true;
}

const SPAWN_RINGS: { r: number; n: number }[] = [
  { r: 8, n: 12 },
  { r: 14, n: 14 },
  { r: 20, n: 16 },
  { r: 28, n: 16 },
  { r: 36, n: 16 },
  { r: 46, n: 12 },
];
const SPAWN_MARGIN = PLAYER_RADIUS + 0.4;

function buildSpawns(boxes: readonly Aabb[]): Vec3[] {
  const spawns: Vec3[] = [];
  for (const { r, n } of SPAWN_RINGS) {
    for (let i = 0; i < n; i++) {
      // A small per-ring phase offset keeps successive rings' points from lining up on the same
      // radial spokes, so the crowd reads as scattered rather than gridded.
      const a = (i / n) * Math.PI * 2 + r * 0.013;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      // Boxes whose top is a step (the well curb, a kerb) are walked over, not around: filtering
      // spawns against them would punch holes in the ring for no reason.
      if (
        clearOf(
          boxes.filter((b) => b.max.y > 0.5),
          x,
          z,
          SPAWN_MARGIN,
        )
      )
        spawns.push({ x, y: 0, z });
    }
  }
  return spawns;
}

// ---------------------------------------------------------------------------------------------
// Waypoints: a small hub-and-spoke graph so anything that walks (an NPC's future patrol, a
// director hymn) has nodes to walk on. Not load-bearing for the perf gate (players don't path).
// ---------------------------------------------------------------------------------------------
function buildWaypoints(): WaypointGraph {
  const nodes: Vec3[] = [];
  const edges: [number, number][] = [];
  const addNode = (x: number, z: number): number => {
    nodes.push({ x, y: 0, z });
    return nodes.length - 1;
  };
  const link = (a: number, b: number): void => {
    edges.push([a, b]);
  };

  const nGate = addNode(0, -52);
  const nSquare = addNode(0, 0);
  const nHallApproach = addNode(0, 36);
  const nHall = addNode(0, 44);
  const nWest = addNode(-45, 0);
  const nEast = addNode(45, 0);
  const nStall = addNode(STALL_X, STALL_Z + 3);
  const nGraveyardApproach = addNode(-30, 30);
  const nGraveyard = addNode(GY_CX, GY_CZ - 8);

  link(nGate, nSquare);
  link(nSquare, nHallApproach);
  link(nHallApproach, nHall);
  link(nSquare, nWest);
  link(nSquare, nEast);
  link(nEast, nStall);
  link(nSquare, nGraveyardApproach);
  link(nGraveyardApproach, nGraveyard);
  link(nWest, nGraveyardApproach);

  return { nodes, edges };
}

// ---------------------------------------------------------------------------------------------
// Named areas. Every pos.y is 0 - the ground under it - rather than a mid-room offset: the city
// is flat, and `halfHeight` alone (not a raised centre) is what buys each cylinder its vertical
// reach. The Lantern Hall gets a taller halfHeight (6, not 4) so its reach clears the 6 m roof.
// ---------------------------------------------------------------------------------------------
const areas: AreaDef[] = [
  {
    id: 1,
    name: 'the Lantern Hall',
    pos: { x: HALL_CX, y: 0, z: HALL_CZ },
    radius: 13,
    // 12, not the greybox's 6: `lanternfall_region.py` builds the Hall with 8 m walls and a 12 m
    // ridge so it is what a player's eye lands on from the gate, and a 6 m cylinder would stop at
    // the eaves — an objective pinned to "the Lantern Hall" has to contain its roof.
    halfHeight: 12,
  },
  { id: 2, name: 'the Square', pos: { x: 0, y: 0, z: 0 }, radius: 35, halfHeight: 4 },
  { id: 3, name: 'the Gate', pos: { x: 0, y: 0, z: -58 }, radius: 10, halfHeight: 4 },
  { id: 4, name: 'the Graveyard', pos: { x: GY_CX, y: 0, z: GY_CZ }, radius: 12, halfHeight: 4 },
];

/** A sidecar stand by id, or the in-code position when the Blender pass has not placed one. */
function standOr(region: RegionSidecar | undefined, id: string, fallback: Vec3, facing: Vec3) {
  const stand = region?.npcStands.find((s) => s.id === id);
  if (stand) return { pos: stand.pos, yaw: stand.yaw };
  return { pos: fallback, yaw: faceTowards(fallback, facing) };
}

export interface LanternfallBuildOptions {
  /** A parsed region sidecar (region.ts). Omit for the in-code greybox. */
  region?: RegionSidecar;
}

function buildLanternfall(opts: LanternfallBuildOptions = {}): MapDef {
  const region = opts.region;
  const boxes: Aabb[] = region
    ? region.boxes.map((b) => ({ min: b.min, max: b.max }))
    : greyboxBoxes();
  const terrain: Heightfield = region?.heightfield ?? FLAT_TERRAIN;
  const world: World = { groundY: 0, boxes, terrain };

  const centre = { x: 0, y: 0, z: 0 };
  const isolde = standOr(region, 'isolde', isoldePos, centre);
  const sabel = standOr(region, 'sabel', sabelPos, centre);

  const npcs: NpcDef[] = [
    {
      id: 1,
      name: 'Isolde Vane',
      role: 'the Lantern Hall',
      // char-04 (Erika Archer): the same body she wears in the Yard and on the Spine - hooded,
      // layered cloth, the only silhouette in the library that reads as somebody who stays put.
      body: 'char-04',
      pos: isolde.pos,
      yaw: isolde.yaw, // faces the door, out into the Square
      bark: "Work's posted here for anyone walking out that gate. Come back and tell me what you found, or don't come back at all.",
      voice: 'isolde',
    },
    {
      id: 2,
      name: 'Sabel Roque',
      role: 'Quartermaster',
      // char-02: the same kitted, ledger-carrying body she has on the Spine.
      body: 'char-02',
      pos: sabel.pos,
      yaw: sabel.yaw, // faces the Square
      bark: "Bring me what you pull out of the Downs. I weigh it, I pay for it, and I don't ask where the rest went.",
      voice: 'sabel',
      // She keeps all three counters in Lanternfall (see `vendor.ts`'s `SABEL_NPC_ID`): the market
      // panel opens on her, and hers is the only bench that will take Wick for a repair.
      vendor: true,
    },
  ];

  const map: MapDef = {
    id: 'lanternfall',
    name: 'Lanternfall',
    persistent: true,
    maxPlayers: 500,
    world,
    spawns: buildSpawns(boxes),
    graveyards: [region?.graveyards[0] ?? GRAVEYARD_POS],
    /**
     * The one way out: north through the Gate. `region` sits centred in the 10 m gap (radius 6
     * slightly overlaps the wall jambs either side, which is harmless - a player can't stand
     * inside a wall box, so that sliver of the cylinder is simply dead trigger space).
     *
     * `spawn` (0, 0, 215) is where a player going the other way lands: just inside the Downs'
     * south gate, on the Lantern Road, 24 m clear of that map's own exit region. See
     * vigil-downs.ts's matching exit for the numbers this must agree with.
     */
    exits: [
      {
        id: 1,
        name: 'the Vigil Downs',
        to: 'vigil-downs',
        region: { pos: { x: 0, y: 2, z: -60 }, radius: 6, halfHeight: 4 },
        spawn: { x: 0, y: 0, z: 215 },
        yaw: 0, // north, into the Downs
      },
    ],
    defaultAreaName: 'Lanternfall',
    areas,
    npcs,
    waypoints: buildWaypoints(),
  };
  // With a sidecar every collider's visuals come from the GLB, so the client greyboxes none of
  // them. Without one the key is absent and `render/scene.ts` batches the lot, as before.
  if (region) map.regionBoxes = boxes;
  return map;
}

/**
 * Lanternfall. A singleton: `MAPS`, every room, the bots' nav cache and the client all hold this
 * object, so `applyLanternfallRegion` rebuilds it *in place* rather than replacing it.
 */
export const LANTERNFALL: MapDef = buildLanternfall();

/**
 * Re-authors `LANTERNFALL` against a region sidecar (or back to the greybox with `null`). Call it
 * once at startup, before any room or prediction exists - a room that captured `LANTERNFALL.world`
 * earlier keeps colliding against the old geometry.
 */
export function applyLanternfallRegion(region: RegionSidecar | null): MapDef {
  const next = buildLanternfall(region ? { region } : {});
  // `regionBoxes` is the one optional key: drop it explicitly when reverting so the greybox map
  // doesn't keep a stale list from a previous apply.
  delete LANTERNFALL.regionBoxes;
  Object.assign(LANTERNFALL, next);
  return LANTERNFALL;
}
